/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { isElementNull } from '../../../../utils';

import { VehicleMarkerInfo, LatLng, IMapContextMenuData, IMapContextMenuPermissions } from '../../../core/models';

@Component({
  selector: 'fm-vehicle-menu-container',
  templateUrl: './vehicle-menu-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VehicleMenuContainerComponent implements OnChanges {
  @Input()
  public menuData: IMapContextMenuData<VehicleMarkerInfo>;

  @Input()
  public userPermissions: IMapContextMenuPermissions;

  @Input()
  public isPingMVPEnabled: boolean;

  @Output()
  public closed: EventEmitter<void> = new EventEmitter();

  @Output()
  public findNearestClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public viewReplayClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public runDailyReportClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public runDetailedReportClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public zoomToClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public streetViewClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public createGeofenceClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public removeFromLiveMapClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public getDirectionToClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public getDirectionFromClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public editDriverClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public editVehicleClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public sendMessageClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public sendStopClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public viewStopsClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public viewMessagesClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public locationToGarminClicked: EventEmitter<LatLng> = new EventEmitter();

  @Output()
  public roadsideAssistanceClicked: EventEmitter<LatLng> = new EventEmitter();

  @Output()
  public copyCoordinatesClicked: EventEmitter<string> = new EventEmitter();

  @Output()
  public troubleshootVehicleClicked: EventEmitter<LatLng> = new EventEmitter();

  public showEditVehicle = false;
  public showEditDriver = false;
  public showSendMessage = false;
  public editMenuItems = 0;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.userPermissions?.currentValue || changes?.menuData.currentValue) {
      this.showEditVehicle = this.userPermissions?.editVehicle;
      this.showEditDriver = this.userPermissions?.editDriver && !isElementNull(this.menuData?.markerInfo?.driverId);
      this.showSendMessage = !isElementNull(this.menuData?.markerInfo?.driverId);
      this.editMenuItems = [this.showEditDriver, this.showSendMessage, this.showEditVehicle].filter(val => val === true).length;
    }
  }

  public onMenuClosed(): void {
    this.closed.emit();
  }

  public onFindNearestClicked(): void {
    this.findNearestClicked.emit();
    this.closed.emit();
  }

  public onViewReplayClicked(): void {
    this.viewReplayClicked.emit();
    this.closed.emit();
  }

  public onRunDailyReportClicked(): void {
    this.runDailyReportClicked.emit();
    this.closed.emit();
  }

  public onRunDetailedReportClicked(): void {
    this.runDetailedReportClicked.emit();
    this.closed.emit();
  }

  public onZoomToClicked(): void {
    this.zoomToClicked.emit();
    this.closed.emit();
  }

  public onStreetViewClicked(): void {
    if (this.menuData.streetViewAvailable) {
      this.streetViewClicked.emit();
      this.closed.emit();
    }
  }

  public onCreateGeofenceClicked(): void {
    this.createGeofenceClicked.emit();
    this.closed.emit();
  }

  public onRemoveFromLiveMapClicked(): void {
    this.removeFromLiveMapClicked.emit();
    this.closed.emit();
  }

  public onGetDirectionToClicked(): void {
    this.getDirectionToClicked.emit();
    this.closed.emit();
  }

  public onGetDirectionFromClicked(): void {
    this.getDirectionFromClicked.emit();
    this.closed.emit();
  }

  public onEditDriverClicked(): void {
    this.editDriverClicked.emit();
    this.closed.emit();
  }

  public onEditVehicleClicked(): void {
    this.editVehicleClicked.emit();
    this.closed.emit();
  }

  public onSendMessageClicked(): void {
    this.sendMessageClicked.emit();
    this.closed.emit();
  }

  public onSendStopClicked(): void {
    this.sendStopClicked.emit();
    this.closed.emit();
  }

  public onRoadsideAssistanceClicked(): void {
    this.roadsideAssistanceClicked.emit();
    this.closed.emit();
  }

  public onTroubleshootVehicleClicked(): void {
    this.troubleshootVehicleClicked.emit();
    this.closed.emit();
  }

  public onViewStopsClicked(): void {
    this.viewStopsClicked.emit();
    this.closed.emit();
  }

  public onViewMessagesClicked(): void {
    this.viewMessagesClicked.emit();
    this.closed.emit();
  }

  public onLocationToGarminClicked(): void {
    this.locationToGarminClicked.emit(this.menuData.clickLatLng);
    this.closed.emit();
  }

  public onCopyCoordinates(coordinatesToCopy: string): void {
    this.copyCoordinatesClicked.emit(coordinatesToCopy);
  }
}
